import * as React from "react";
import { useMemo, useRef } from "react";

import { Cross2Icon } from "@radix-ui/react-icons";
import { useAtom } from "jotai";

import { Label } from "src/components/ui/label";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "src/components/ui/tooltip";
import { cn } from "src/lib/utils";

/**
 * MetricsNumberInput component is a custom input field for entering numeric values.
 * It includes a label, an icon, and a reset button.
 * Value state is managed using Jotai atom.
 * The input value is formatted using a provided format function.
 *
 * @param {{
 *  title: string,
 *  icon: React.ElementType,
 *  defaultValue: number,
 *  formatFn: (v: number) => string,
 *  field: string,
 *  atom: any,
 *  disabled?: boolean,
 *  queryVariableMapping?: (v: number) => any,
 *  onSubmit: () => void,
 * }} props
 * @returns {JSX.Element}
 */
export const MetricsNumberInput = ({
  title,
  icon: Icon,
  defaultValue: _defaultValue = 0,
  formatFn = (v) => String(v),
  field,
  atom,
  disabled,
  queryVariableMapping = (v) => v,
  onSubmit,
}) => {
  const inputRef = useRef(null);

  const [store, setStore] = useAtom(atom);
  const setValueInStore = (newValue) => {
    setStore({
      ...store,
      [field]: newValue,
      ...(queryVariableMapping && {
        queryVariables: {
          ...store.queryVariables,
          [field]: queryVariableMapping(newValue),
        },
      }),
    });
  };

  const initialValueRef = useRef(null);
  const value = useMemo(() => {
    if (inputRef.current === document.activeElement) {
      return store[field];
    }

    const numericValue =
      typeof store[field] === "string" ? Number(store[field]) : store[field];
    return formatFn(numericValue || _defaultValue);
  }, [formatFn, store, field, _defaultValue]);
  const defaultValue = useMemo(
    () => formatFn(_defaultValue),
    [_defaultValue, formatFn]
  );

  const onBlur = () => {
    const numericValue = Number(store[field]);
    if (!Number.isNaN(numericValue)) {
      setValueInStore(numericValue);
    }

    if (store[field] !== initialValueRef.current) {
      onSubmit?.();
    }
  };

  const onChange = (e) => {
    const cleanedValue = e.target.value.replace(/[^0-9.]/g, "");

    if (cleanedValue === "" || cleanedValue.endsWith(".")) {
      setValueInStore(cleanedValue);
      return;
    }

    const newValue = Number(cleanedValue);

    if (Number.isNaN(newValue) || newValue > 999_999_999) {
      return;
    }

    setValueInStore(newValue);
  };

  const onFocus = (e) => {
    e.target.select();

    initialValueRef.current = store.queryVariables?.[field] || store[field];
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();

      e.target.blur();
    }
  };

  const onReset = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setValueInStore(_defaultValue);

    inputRef.current.focus();
    setTimeout(() => {
      inputRef.current.blur();
    }, 0);
  };

  // remove once widely compatible: https://caniuse.com/?search=field-sizing
  const inputWidth = (value?.length || 1) * 1.15;
  return (
    <span
      className={cn(
        "border-input bg-background dark:bg-input/30 focus-within:bg-accent focus-within:ring-ring hover:bg-accent hover:text-accent-foreground inline-flex h-8 cursor-pointer items-center justify-center gap-2 rounded-md border px-2.5 text-xs font-medium whitespace-nowrap shadow-sm transition-colors focus-within:ring-1 focus-within:outline-none",
        disabled && "pointer-events-none cursor-not-allowed opacity-50",
        value === defaultValue
          ? "text-muted-foreground border-dashed shadow-none"
          : "text-foreground shadow-sm"
      )}
      onClick={() => {
        inputRef.current.focus();
      }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          inputRef.current.focus();
        }
      }}
    >
      {Icon && <Icon className="size-4" />}

      {title && (
        <Label className="cursor-pointer text-sm font-normal" htmlFor={field}>
          {title}
        </Label>
      )}
      <input
        name={field}
        id={field}
        ref={inputRef}
        value={value || ""}
        onChange={onChange}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        size={inputWidth}
        disabled={disabled}
        autoComplete="off"
        className="placeholder:text-muted-foreground -mx-1 [field-sizing:content] rounded-xs border-none bg-transparent p-1 text-xs transition-colors focus:ring-0 focus-visible:ring-0 focus-visible:outline-none"
      />

      {/* Reset button */}
      {value !== defaultValue && (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onMouseDown={onReset}
              disabled={disabled}
              className="hover:text-muted-foreground cursor-pointer"
            >
              <Cross2Icon className="size-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent>Reset to default</TooltipContent>
        </Tooltip>
      )}
    </span>
  );
};
